import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { creators } from "../../mocks/magazine";
import { Link } from "react-router-dom";
import SEOHead from "../../components/feature/SEOHead";
import JsonLd from "../../components/feature/JsonLd";

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? "";

const creatorsSchema: Record<string, unknown>[] = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Featured Creators | The Magazine Co.",
    url: `${siteUrl}/creators`,
    description:
      "Meet the independent writers, photographers, and creators who publish their own print magazines through The Magazine Co. — delivering fresh content to subscribers worldwide.",
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
        { "@type": "ListItem", position: 2, name: "Creators", item: `${siteUrl}/creators` },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Independent Creator Magazines — The Magazine Co.",
    url: `${siteUrl}/creators`,
    numberOfItems: creators.length,
    itemListElement: creators.map((creator, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Periodical",
        name: creator.magazineTitle,
        description: `${creator.niche} print magazine by ${creator.name}. Published ${creator.frequency.toLowerCase()}.`,
        author: {
          "@type": "Person",
          name: creator.name,
        },
        publisher: {
          "@type": "Organization",
          name: "The Magazine Co.",
          url: siteUrl,
        },
        genre: creator.niche,
        inLanguage: "en",
      },
    })),
  },
];

export default function Creators() {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Featured Creators | Independent Print Magazine Publishers — The Magazine Co."
        description="Meet the writers, photographers, and independent creators publishing their own print magazines through The Magazine Co. Fresh content. Real people. Every issue."
        keywords="independent magazine creators, print magazine publishers, creator magazines, independent publishing"
        path="/creators"
      />
      <JsonLd id="creators-schema" schema={creatorsSchema} />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-44 pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a93d2e5a294ee9fb2c2dbfd0b8a27814.jpg"
            alt="Creator workspace"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/55" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10 text-center">
          <p className="text-[#F0EAD6] text-xs tracking-[0.3em] uppercase font-semibold mb-5">The Magazines</p>
          <h1 className="text-5xl md:text-7xl font-serif font-light text-white leading-[1.1] mb-8">
            Real Content.<br /><em className="italic">Made to Last.</em>
          </h1>
          <p className="text-white/75 text-lg md:text-xl font-light max-w-2xl mx-auto leading-relaxed">
            Writers, photographers, illustrators, obsessives of every kind — turning what they love into something you can actually hold in your hands.
          </p>
        </div>
      </section>

      {/* Creators Grid */}
      <section className="bg-[#F8F4EE] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <p className="text-[#C41E3A] text-xs tracking-[0.3em] uppercase font-semibold mb-3">On The Press</p>
            <h2 className="text-3xl md:text-4xl font-serif font-light text-[#2B2B2B]">Fresh Content. Real People. Every Issue.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {creators.map((creator) => (
              <article key={creator.id} className="bg-white rounded-2xl overflow-hidden group">
                {/* Magazine Cover */}
                <div className="relative overflow-hidden" style={{ height: "320px" }}>
                  <img
                    src={creator.cover}
                    alt={creator.magazineTitle}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <span className="absolute top-4 right-4 px-3 py-1 rounded-full bg-[#C41E3A] text-white text-xs font-semibold">
                    {creator.frequency}
                  </span>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h2 className="font-serif text-2xl text-white leading-tight">{creator.magazineTitle}</h2>
                    <p className="text-white/75 text-sm mt-1">{creator.niche}</p>
                  </div>
                </div>

                {/* Creator Info */}
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      <img src={creator.avatar} alt={creator.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#2B2B2B] text-sm">{creator.name}</p>
                      <p className="text-[#8B8B8B] text-xs">{creator.niche}</p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-5 p-4 bg-[#F8F4EE] rounded-xl">
                    <div>
                      <p className="text-lg font-serif text-[#C41E3A]">{creator.subscribers}</p>
                      <p className="text-xs text-[#8B8B8B]">Subscribers</p>
                    </div>
                    <div>
                      <p className="text-lg font-serif text-[#C41E3A]">{creator.issues}</p>
                      <p className="text-xs text-[#8B8B8B]">Issues Dropped</p>
                    </div>
                  </div>

                  {/* Quote */}
                  <blockquote className="border-l-2 border-[#C41E3A] pl-4 text-[#6B6B6B] text-sm italic leading-relaxed">
                    &ldquo;{creator.quote}&rdquo;
                  </blockquote>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Join Section */}
      <section className="bg-white py-24 border-t border-[#E8E2DA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#C41E3A] text-xs tracking-[0.3em] uppercase font-semibold mb-5">Want In? Good.</p>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-[#1C1C1C] leading-tight mb-6">
              Your Content<br /><em className="italic">Deserves a Page</em>
            </h2>
            <p className="text-[#5A5A5A] text-base leading-relaxed mb-6">
              Doesn&apos;t matter if you have 500 followers or 500,000 — if you create consistently and your audience actually gives a damn, we want to talk. All niches welcome.
            </p>
            <ul className="flex flex-col gap-4 mb-8">
              {[
                "No minimum subscriber count — ever",
                "Real editorial support from people who get it",
                "Choose your own cadence: weekly, monthly, or quarterly",
                "Your own account manager from day one",
                "Global distribution, handled entirely by us",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-[#4A4A4A]">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0 text-[#C41E3A]">
                    <i className="ri-check-line" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/apply"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#C41E3A] text-white text-sm font-semibold tracking-wide whitespace-nowrap hover:bg-[#A0182E] transition-colors cursor-pointer"
            >
              Apply Now — It&apos;s Free
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {creators.slice(0, 4).map((creator) => (
              <div key={creator.id} className="rounded-xl overflow-hidden" style={{ aspectRatio: "3/4" }}>
                <img
                  src={creator.cover}
                  alt={creator.magazineTitle}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
